import React, { useState } from 'react';
import { Box, TextField, Button, Chip, Typography } from '@mui/material';

/**
 * Multi-value chip input. User types a value, presses Enter or "Add",
 * and it appears as a deletable chip.
 *
 * Props:
 *   label       - field label
 *   values      - string[] | number[]  current values
 *   onChange    - (newValues) => void
 *   helperText  - optional description
 *   type        - 'text' | 'number'
 *   placeholder - input placeholder text
 *   maxValues   - if set, caps how many items can be added
 */
export default function TagsInput({
  label,
  values = [],
  onChange,
  helperText,
  type = 'text',
  placeholder = 'Type and press Enter or Add',
  maxValues
}) {
  const [inputValue, setInputValue] = useState('');

  const atMax = maxValues != null && values.length >= maxValues;

  const addValue = () => {
    if (atMax) return;
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    const parsed = type === 'number' ? Number(trimmed) : trimmed;
    if (!values.map(String).includes(String(parsed))) {
      onChange([...values, parsed]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue();
    }
  };

  const handleDelete = (val) => {
    onChange(values.filter((v) => String(v) !== String(val)));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {label && (
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          type={type}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={atMax}
          sx={{ flex: 1, maxWidth: 300 }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={addValue}
          disabled={atMax}
        >
          Add
        </Button>
      </Box>

      {values.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {values.map((v) => (
            <Chip
              key={String(v)}
              label={String(v)}
              onDelete={() => handleDelete(v)}
              size="small"
            />
          ))}
        </Box>
      )}

      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
